import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';

const SHEET_ENDPOINT = 'https://script.google.com/macros/s/AKfycbzkPwZSQaeldDclIrZFE8ojPhzARQ4ELEwaeeS5ZDl2G0yDWbu_Q1fQ4XJkHP_WHyhR/exec';

export default function ContactForm({ onSuccessClose }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submittedData, setSubmittedData] = useState(null);
    const [extraFieldsRevealed, setExtraFieldsRevealed] = useState(false);

    const [formData, setFormData] = useState({
        firstName: '',
        email: '',
        phoneNumber: '',
        companyName: '',
        service: '',
        message: ''
    });

    const [errors, setErrors] = useState({});

    // Progressive reveal: show extra fields once default required fields are filled
    useEffect(() => {
        if (
            formData.firstName.trim() !== '' &&
            formData.email.trim() !== '' &&
            formData.phoneNumber.trim() !== ''
        ) {
            setExtraFieldsRevealed(true);
        }
    }, [formData.firstName, formData.email, formData.phoneNumber]);

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isValidPhoneNumber = (phoneNumber) => /^[+]?[\d\s\-()]{7,15}$/.test(phoneNumber);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        // If extra fields not yet revealed, validate first 3 fields and reveal rest
        if (!extraFieldsRevealed) {
            const earlyErrors = {};
            if (!formData.firstName.trim()) earlyErrors.firstName = 'First Name is required';
            if (!formData.email.trim()) earlyErrors.email = 'Email is required';
            else if (!isValidEmail(formData.email)) earlyErrors.email = 'Invalid email format';
            if (!formData.phoneNumber.trim()) earlyErrors.phoneNumber = 'Phone Number is required';
            else if (!isValidPhoneNumber(formData.phoneNumber)) earlyErrors.phoneNumber = 'Invalid phone number format';
            setErrors(earlyErrors);
            if (Object.keys(earlyErrors).length === 0) setExtraFieldsRevealed(true);
            return;
        }

        const newErrors = {};
        if (!formData.firstName.trim()) newErrors.firstName = 'First Name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!isValidEmail(formData.email)) newErrors.email = 'Invalid email format';
        if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone Number is required';
        else if (!isValidPhoneNumber(formData.phoneNumber)) newErrors.phoneNumber = 'Invalid phone number format';
        if (!formData.message.trim()) newErrors.message = 'Message is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);

        const payload = { ...formData, sheetName: 'Contact Leads' };

        Promise.allSettled([
            fetch('https://thespinnennetz.com/sn-forms/contact.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            }),
            fetch(SHEET_ENDPOINT, {
                method: 'POST',
                mode: 'no-cors',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            })
        ])
            .then(() => {
                setSubmittedData({ ...formData });
                setFormData({
                    firstName: '',
                    email: '',
                    phoneNumber: '',
                    companyName: '',
                    service: '',
                    message: ''
                });
                setIsSubmitting(false);
                setIsSubmitted(true);
            })
            .catch(error => {
                setIsSubmitting(false);
                console.error('There was a problem with your fetch operation:', error);
                setSubmittedData({ ...formData });
                setIsSubmitted(true);
            });
    };

    // Success state — matches book audit success design
    if (isSubmitted && submittedData) {
        const dName = submittedData.firstName || 'there';
        const dCompany = submittedData.companyName ? <span> from <strong>{submittedData.companyName}</strong></span> : '';
        const dEmail = submittedData.email || 'your email';

        return (
            <div className="audit-success-state">
                <div className="success-icon-badge">✓</div>
                <h3 className="success-title">Message Received!</h3>
                <p className="success-desc">
                    Thank you, <strong>{dName}</strong>{dCompany}. We've received your message and will get back to you at <strong>{dEmail}</strong> within 24 hours.
                </p>
                <div style={{ marginTop: '24px' }}>
                    <Link 
                        to="/" 
                        className="sn-submit-btn" 
                        style={{ textDecoration: 'none' }}
                        onClick={(e) => {
                            if (window.location.pathname === '/' || window.location.pathname === '') {
                                e.preventDefault(); // Don't reload if already on home
                            }
                            if (onSuccessClose) {
                                onSuccessClose(); // Close modal
                            }
                        }}
                    >
                        Back to Home Page
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <h2 className="audit-card-title">Let's Discuss Your Project</h2>
            <p className="audit-card-desc">
                Get a free consultation and receive a custom quote within 24 hours.
            </p>

            <form onSubmit={handleSubmit} className="audit-form" noValidate>
                {/* Row 1: First Name */}
                <Row>
                    <Col md={12}>
                        <div className="sn-floating-group">
                            <input
                                id="contact-firstName"
                                type="text"
                                name="firstName"
                                className={`sn-form-control ${errors.firstName ? 'is-invalid' : ''}`}
                                placeholder=" "
                                value={formData.firstName}
                                onChange={handleChange}
                            />
                            <label htmlFor="contact-firstName" className={errors.firstName ? 'label-error' : ''}>First Name</label>
                            {errors.firstName && <div className="sn-invalid-feedback">{errors.firstName}</div>}
                        </div>
                    </Col>
                </Row>

                {/* Row 2: Email | Phone */}
                <Row>
                    <Col md={6}>
                        <div className="sn-floating-group">
                            <input
                                id="contact-email"
                                type="email"
                                name="email"
                                className={`sn-form-control ${errors.email ? 'is-invalid' : ''}`}
                                placeholder=" "
                                value={formData.email}
                                onChange={handleChange}
                            />
                            <label htmlFor="contact-email" className={errors.email ? 'label-error' : ''}>Email Address</label>
                            {errors.email && <div className="sn-invalid-feedback">{errors.email}</div>}
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="sn-floating-group">
                            <input
                                id="contact-phone"
                                type="tel"
                                name="phoneNumber"
                                className={`sn-form-control ${errors.phoneNumber ? 'is-invalid' : ''}`}
                                placeholder=" "
                                value={formData.phoneNumber}
                                onChange={handleChange}
                            />
                            <label htmlFor="contact-phone" className={errors.phoneNumber ? 'label-error' : ''}>Phone Number</label>
                            {errors.phoneNumber && <div className="sn-invalid-feedback">{errors.phoneNumber}</div>}
                        </div>
                    </Col>
                </Row>

                {/* Extra fields — revealed once default fields are filled */}
                {extraFieldsRevealed && (
                    <div className="extra-fields-section">
                        <Row>
                            <Col md={6}>
                                <div className="sn-floating-group">
                                    <input
                                        id="contact-company"
                                        type="text"
                                        name="companyName"
                                        className="sn-form-control"
                                        placeholder=" "
                                        value={formData.companyName}
                                        onChange={handleChange}
                                    />
                                    <label htmlFor="contact-company">Company Name (Optional)</label>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className={`sn-floating-group ${formData.service ? 'filled' : ''}`}>
                                    <select
                                        id="contact-service"
                                        name="service"
                                        className="sn-form-control"
                                        value={formData.service}
                                        onChange={handleChange}
                                        style={{ appearance: 'none', backgroundColor: 'transparent' }}
                                    >
                                        <option value="" disabled hidden></option>
                                        <option value="Shopify Store Development">Shopify Store Development</option>
                                        <option value="Shopify Store Redesign">Shopify Store Redesign</option>
                                        <option value="Shopify Theme Customization">Shopify Theme Customization</option>
                                        <option value="WordPress Website Development">WordPress Website Development</option>
                                        <option value="WordPress Website Redesign">WordPress Website Redesign</option>
                                        <option value="Website Maintenance">Website Maintenance</option>
                                        <option value="Conversion Landing Pages">Conversion Landing Pages</option>
                                        <option value="CRO Sprint">CRO Sprint</option>
                                        <option value="Monthly CRO Retainer">Monthly CRO Retainer</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    <label htmlFor="contact-service">Service Interested In (Optional)</label>
                                    <div style={{ position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#666' }}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <div className="sn-floating-group">
                            <textarea
                                id="contact-message"
                                name="message"
                                rows="3"
                                className={`sn-form-control sn-textarea ${errors.message ? 'is-invalid' : ''}`}
                                placeholder=" "
                                value={formData.message}
                                onChange={handleChange}
                            ></textarea>
                            <label htmlFor="contact-message" className={errors.message ? 'label-error' : ''}>Message</label>
                            {errors.message && <div className="sn-invalid-feedback">{errors.message}</div>}
                        </div>
                    </div>
                )}

                <button type="submit" className="sn-submit-btn" disabled={isSubmitting}>
                    {isSubmitting ? (
                        <>Submitting…</>
                    ) : (
                        <>
                            Get Free Consultation{' '}
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle' }}>
                                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </>
                    )}
                </button>
            </form>

            {/* Trust lines below CTA */}
            <div className="contact-trust-lines">
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 6L9 17L4 12" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg> Response within 24 hours</span>
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 6L9 17L4 12" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg> No spam, ever</span>
                <span>🔒 Your information is secure</span>
            </div>
        </>
    );
}
